import { useState, useEffect } from 'react';
import { Text, Cell, Spacer } from '@vinted/web-ui';
import { Translation } from 'constants/translation';
import { ClickableElement } from 'constants/tracking/clickable-elements';
import { getUserById } from 'data/api';
import useFetch from 'hooks/useFetch';
import useTranslate from 'hooks/useTranslate';
import { LinkifiedMessage } from 'components/LinkifiedMessage';
import TranslationControl from 'components/TranslationControl';
import { onA11yKeyDown } from 'data/utils/a11y';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const MAX_SYMBOL_COUNT = 220;

function truncateDescription(description) {
  return description.substring(0, MAX_SYMBOL_COUNT).split('\n')[0].trim();
}

const UserDescription = _ref => {
  let {
    user,
    isContentTranslationEnabled
  } = _ref;
  const translate = useTranslate('user.description');
  const {
    0: isTruncated,
    1: setIsTruncated
  } = useState(false);
  const {
    0: isContentTranslated,
    1: setIsContentTranslated
  } = useState(false);
  const {
    0: translatedDescription,
    1: setTranslatedDescription
  } = useState('');
  const {
    fetch: fetchUserById,
    data: userData
  } = useFetch(getUserById);
  useEffect(() => {
    if (!user.about) return;
    const truncatedDescription = truncateDescription(user.about);

    if (user.about !== truncatedDescription) {
      setIsTruncated(true);
    }
  }, [user.about]);
  useEffect(() => {
    if (!userData) return;
    const {
      about
    } = userData.user;
    if (!about) return;
    setTranslatedDescription(about);
    setIsContentTranslated(true);
  }, [userData]);

  async function translateDescription() {
    if (translatedDescription) {
      setIsContentTranslated(true);
      return;
    }

    await fetchUserById({
      id: user.id,
      localize: true
    });
  }

  function showOriginal() {
    setIsContentTranslated(false);
  }

  function handleTranslate(isTranslated) {
    return isTranslated ? showOriginal() : translateDescription();
  }

  function toggleIsTruncated() {
    setIsTruncated(prevIsTruncated => !prevIsTruncated);
  }

  function handleKeyDown(event) {
    onA11yKeyDown(event, toggleIsTruncated);
  }

  const fullDescription = isContentTranslated && translatedDescription ? translatedDescription : user.about;
  if (!fullDescription) return null;
  const activeDescription = isTruncated ? "".concat(truncateDescription(fullDescription), "...") : fullDescription;
  const showTranslationControl = isContentTranslationEnabled && user.localization !== Translation.None;
  return /*#__PURE__*/_jsxs(Cell, {
    children: [/*#__PURE__*/_jsx(Text, {
      format: true,
      children: /*#__PURE__*/_jsxs("div", {
        className: "profile__user-description",
        "data-testid": "profile-user-description",
        children: [/*#__PURE__*/_jsx(LinkifiedMessage, {
          text: activeDescription
        }), isTruncated && /*#__PURE__*/_jsx("span", {
          className: "u-ui-margin-left-small",
          onClick: toggleIsTruncated,
          onKeyDown: handleKeyDown,
          "data-testid": "more-button",
          children: /*#__PURE__*/_jsx(Text, {
            clickable: true,
            children: translate('actions.more')
          })
        })]
      })
    }), showTranslationControl && /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(Spacer, {}), /*#__PURE__*/_jsx(TranslationControl, {
        isTranslated: isContentTranslated,
        autotranslate: false,
        onClick: handleTranslate,
        tracking: {
          target: ClickableElement.TranslateDescription,
          targetDetails: user.id.toString()
        }
      })]
    })]
  });
};

export default UserDescription;